import { Box, Paper } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { BannerCloseType, BannerCloseTypes, BannerKey } from 'src/Libs/banners/types'
import CloseAction from 'src/Libs/banners/components/BannerCloseAction'
import { Typography } from '@e3dc-react/shell/Elements/Typography'

const useStyles = makeStyles()(theme => ({
  container: {
    position: 'relative',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4, 2, 4),
    alignItems: 'center',
    boxShadow: theme.effectStyles.shadowMain,

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 2, 2, 2),
      gap: theme.spacing(2),
    },
  },
  content: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },

    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(4),
    },

    [theme.breakpoints.up('xl')]: {
      gap: theme.spacing(20),
    },
  },
  rightActionsWrapper: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      minWidth: 220,
    },
  },
  infoText: {
    textAlign: 'center',
    color: theme.palette.text.primary,
    width: '100%',
  },
  button: {
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,

    [theme.breakpoints.up('md')]: {
      marginLeft: 'auto',
    },
  },
  closeAction: {
    color: theme.palette.text.primary,

    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(2),
    },
  },
  iconWrapper: {
    display: 'flex',

    [theme.breakpoints.up('md')]: {
      minWidth: 200,
    },
  },
  hideMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  hideDesktop: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}))

interface Props {
  bannerKey: BannerKey
  i18nKey: string
  values?: Record<string, string | number>
  linkHref?: string
  closeType: BannerCloseType
  containerStyle?: string
  textStyle?: string
  closeActionStyle?: string
  icon?: React.ReactElement /** should not exceed 30*30 size */
  onRemoveSuccessMessageKey?: string
  button?: React.ReactElement
}

/**
 * This component is a standard banner and should be used for displaying general info/warning/alerts.
 *
 * @beta This component is not yet finalized and might be subject to change.
 * @see {@link 'https://www.figma.com/design/2XkUgderL8Y8WDXHPhUXNJ/E3DC-Component-Catalog?node-id=12-5365&node-type=canvas&t=THRggXi35mMu1E92-0'}
 */
const Banner: React.FC<Props> = ({
  bannerKey,
  icon,
  button,
  i18nKey,
  values,
  linkHref,
  closeType,
  onRemoveSuccessMessageKey,
  containerStyle,
  textStyle,
  closeActionStyle,
}) => {
  const { cx, classes } = useStyles()

  return (
    <Paper className={cx(classes.container, containerStyle)}>
      <Box className={classes.content}>
        <Box className={classes.iconWrapper}>{icon}</Box>
        <Typography
          variant="body1"
          className={cx(classes.infoText, textStyle)}
          values={values}
          components={{
            strong: <Box component="strong" />,
            a: <Box component={'a'} href={linkHref} sx={{ textDecoration: 'none', color: 'primary.main' }} />,
            /** custom element for E.ON banner with different font. */
            eon: <Typography fontFamily="EON" fontSize={24} />,
          }}
        >
          {i18nKey}
        </Typography>
        <Box className={cx(classes.button, classes.hideDesktop)}>{button}</Box>
      </Box>
      <Box className={classes.rightActionsWrapper}>
        <Box className={cx(classes.button, classes.hideMobile)}>{button}</Box>
        {closeType !== BannerCloseTypes.Permanent && (
          <Box className={cx(classes.closeAction, closeActionStyle)}>
            <CloseAction closeType={closeType} bannerKey={bannerKey} onRemoveSuccessMessageKey={onRemoveSuccessMessageKey} />
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export default Banner
